import { EventsListPublic } from "../../components/public/EventsListPublic";
import { ProjectsListPublic } from "../../components/public/ProjectsListPublic";
import NewsletterFormPublic from "../../components/public/NewsletterFormPublic";
import HeaderSlidesPublic from "../../components/public/HeaderSlidesPublic";
import { useEffect, useState, useRef } from "react";
import { organization, content, branding } from "../../config";
import axios from "axios";
import FeaturedButtonsPublic from "../../components/public/FeaturedButtonsPublic";
import { SEO } from "../../components/SEO";

export default function HomePublic() {
  const [about, setAbout] = useState({ image: "", header: "", text: "" });
  const [displayNumber, setDisplayNumber] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const statsRef = useRef(null);

  useEffect(() => {
    document.title = organization.name;
  }, []);

  useEffect(() => {
    axios.get("/api/public/about").then((res) => {
      setAbout(res.data.content);
    });
  }, []);

  // Extract number from box_big_text and animate
  useEffect(() => {
    // Check if animation is disabled
    if (about.enable_animation === false) {
      setHasAnimated(true);
      return;
    }

    if (!about.box_big_text || hasAnimated) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            // Extract the number from the text (e.g., "2,740+" -> 2740)
            const match = about.box_big_text.match(/[\d,]+/);
            if (match) {
              const targetNumber = parseInt(match[0].replace(/,/g, ''));
              const duration = 2000; // 2 seconds
              const startTime = Date.now();
              const startNumber = 0;

              const animate = () => {
                const currentTime = Date.now();
                const elapsed = currentTime - startTime;
                const progress = Math.min(elapsed / duration, 1);

                // Easing function for smooth animation
                const easeOutQuart = 1 - Math.pow(1 - progress, 4);
                const current = Math.floor(startNumber + (targetNumber - startNumber) * easeOutQuart);

                setDisplayNumber(current);

                if (progress < 1) {
                  requestAnimationFrame(animate);
                } else {
                  setDisplayNumber(targetNumber);
                  setHasAnimated(true);
                }
              };

              animate();
            }
          }
        });
      },
      { threshold: 0.5 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => {
      if (statsRef.current) {
        observer.unobserve(statsRef.current);
      }
    };
  }, [about.box_big_text, about.enable_animation, hasAnimated]);

  return (
    <>
      <SEO
        title={null}
        description="The Neighborhood Service Center, Inc. is a non-profit Community Action Agency that provides services and assistance to low-income families and elderly in Talbot County, Maryland."
        path="/"
      />
      <HeaderSlidesPublic />

      <FeaturedButtonsPublic />

      {/* About Section overlapping */}
      <section
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          marginTop: "60px",
          padding: "0px 40px",
          fontFamily: "'Montserrat', sans-serif",
        }}
      >
        <style>
          {`
            @media (max-width: 768px) {
              .about-flex-container {
                justify-content: center !important;
                gap: 30px !important;
              }
              .stats-card {
                margin: 0 auto !important;
              }
            }
          `}
        </style>
        <div
          className="about-flex-container"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "60px",
            flexWrap: "wrap",
          }}
        >
          {/* Left side - Text content */}
          <div style={{ flex: "1", minWidth: "300px" }}>
            <h2
              style={{
                color: branding.primaryColor,
                fontWeight: "700",
                marginBottom: "25px",
                // textTransform: "uppercase",
                letterSpacing: "1px",
                fontSize: "3rem",
              }}
            >
              {about.header}
            </h2>
            <p
              style={{
                // color: "#555",
                fontSize: "1.25rem",
                lineHeight: "1.8",
              }}
            >
              {about.text}
            </p>
          </div>

          {/* Right side - Stats card */}
          <div
            ref={statsRef}
            className="stats-card"
            style={{
              flex: "0 0 auto",
              minWidth: "350px",
              padding: "25px 20px",
              backgroundColor: "#c90551",
              color: "white",
              borderRadius: "12px",
              textAlign: "center",
              boxShadow: "0 4px 15px rgba(0, 0, 0, 0.08)",
            }}
          >
            <div style={{ marginBottom: "20px" }}>
              {/* Placeholder for logo - you can add an icon/image here */}
              {/* <div
                style={{
                  width: "80px",
                  height: "80px",
                  margin: "0 auto 20px auto",
                  borderRadius: "50%",
                  backgroundColor: "#E8F4F8",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <span style={{ fontSize: "2rem" }}>📊</span>
              </div> */}
            </div>
            <div
              style={{
                fontSize: "3.5rem",
                fontWeight: "700",
                lineHeight: "1",
                marginBottom: "15px",
                fontVariantNumeric: "tabular-nums",
                fontFeatureSettings: "'tnum'",
              }}
            >
              {(() => {
                // If animation is disabled, just show the original text
                if (about.enable_animation === false) {
                  return about.box_big_text;
                }

                // Extract number and format it
                const match = about.box_big_text?.match(/[\d,]+/);
                if (match && hasAnimated) {
                  // Format the animated number with commas
                  const formattedNumber = displayNumber.toLocaleString();
                  // Replace the number in the original text with the animated one
                  return about.box_big_text.replace(/[\d,]+/, formattedNumber);
                } else if (match && !hasAnimated) {
                  // Show the animated number during animation
                  const formattedNumber = displayNumber.toLocaleString();
                  return about.box_big_text.replace(/[\d,]+/, formattedNumber);
                }
                return about.box_big_text;
              })()}
            </div>
            <p
              style={{
                fontSize: "1.2rem",
                // textTransform: "uppercase",
                letterSpacing: "2px",
                fontWeight: "600",
              }}
            >
              {about.box_small_text}
            </p>
          </div>
        </div>
      </section>

      {/* <section
        style={{
          marginTop: "60px",
          backgroundColor: "#F8F9FA", // full-width background
          padding: "60px 20px",       // add side padding for small screens
          fontFamily: "'Montserrat', sans-serif",
        }}
      >
        <div
          style={{
            maxWidth: "900px",
            margin: "0 auto",
            textAlign: "center",
          }}
        >
          <h2
            style={{
              color: branding.primaryColor,
              fontWeight: "700",
              marginBottom: "20px",
              textTransform: "uppercase",
              letterSpacing: "1px",
            }}
          >
            Upcoming Public Events
          </h2>
          <EventsListPublic />
        </div>
      </section> */}


      <section
        style={{
          marginTop: "0px",
          paddingTop: "50px",
          paddingBottom: "50px",
          paddingLeft: "20px",
          paddingRight: "20px",
          fontFamily: "'Montserrat', sans-serif"
        }}
      >
        <h2
          style={{
            color: branding.primaryColor,
            fontWeight: "700",
            fontSize: "3rem",
            marginBottom: "50px",
            // textTransform: "uppercase",
            letterSpacing: "1px",
          }} className="text-center"
        >
          {content.atAGlanceTitle}
        </h2>
        <ProjectsListPublic />
      </section>

      <NewsletterFormPublic />
    </>
  );
}
